// GENERATED by `pnpm run gen:narrative` from src/core/content/narrative/scenes.md — DO NOT EDIT.
// Hand edits here are drift by definition: edit the markdown source and regenerate
// (the gen-narrative verify gate rebuilds this file and byte-compares it).
// Data only — the types + helpers stay hand-written in scenes.ts, which re-exports this.

import type { SceneDef } from './scenes';
import { NAMES } from './names';
import { NPC_NAME } from './voices';

export const SCENES: readonly SceneDef[] = [
  {
    id: 'nengu-autumn-frame',
    scene: {
      id: 'nengu-autumn-frame',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'Autumn thins to its last day, and the board comes out at an hour it does not usually keep. The whole house comes with it: the family by the book, the hands by the door, and you in the corner — counted as part of the room, and not asked into it.',
        },
        {
          voice: 'narrator',
          text: "Genemon reads the year against the house. The harvest in and weighed, the lease, the winter's wood — and then the one thing the house owes the land, that must be answered before the season is allowed to turn.",
        },
        {
          voice: 'narrator',
          text: 'Nobody reads the sum aloud. It arrives the way weather arrives: the room goes still around it, a breath drawn and not let go, every eye on the book and none on one another.',
        },
        {
          voice: 'narrator',
          text: "Chiyo looks once, and briefly, at a screen that will not be in its room by spring. That is all that is shown of it, and none of it is explained. You are learning the house's true size by what it will let itself be seen without.",
        },
        {
          voice: 'narrator',
          text: 'The book closes. The house has answered for the land; its autumn can end. You are still in the corner. Nobody tells you the season is over. It is.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'scripted' },
  },

  {
    id: 'count',
    scene: {
      id: 'count',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'A hand shakes you awake in the dark of the woodshed. Rokusuke, with a lantern, looking mostly at the door.',
        },
        {
          voice: 'villager',
          speaker: NPC_NAME.rokusuke,
          text: '"Board room. The whole house is called. You as well. I only fetch."',
        },
        {
          voice: 'narrator',
          text: 'Lamps at the board at the wrong hour. The household stands in two rows — the hands by the door, the house by the book — and between the rows there is more floor than the room usually has. You are shown to the middle of it. Nobody arranged that.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The alcove chest stands open. One item short against the book. A packet — cloth, bound with cord, a hand\'s length — entered as lamp-oil for the shrine. Counted at the evening rice. Gone since. The house will be counted before it sleeps."',
        },
        {
          voice: 'narrator',
          text: "Naoyuki stands at his father's place at the board. He does not look at you until he does.",
        },
        {
          voice: 'official',
          speaker: NPC_NAME.naoyuki,
          text: '"If the house must do this, then it does it properly — in order of service, eldest hands first, so that no one can say—"',
        },
        { voice: 'official', speaker: NPC_NAME.naoyuki, text: '"No. The weir man. Him first."' },
        {
          voice: 'narrator',
          text: 'Nobody objects. Some of the hands look at you; more look at the floor. It is the order the room was already thinking in.',
        },
      ],
      topics: [
        {
          id: 'r5-accused',
          label: '"Am I accused?"',
          answer: [
            {
              voice: 'narrator',
              text: 'The question costs you something to ask. Genemon does not notice the price.',
            },
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"You are counted. The whole house is counted. Accusation is for after the book speaks."',
            },
          ],
        },
        {
          id: 'r5-why-first',
          label: '"Why me first?"',
          answer: [
            {
              voice: 'official',
              speaker: NPC_NAME.naoyuki,
              text: '"Because the book carries ten years on every other man here, and on you it carries—"',
            },
            {
              voice: 'official',
              speaker: NPC_NAME.naoyuki,
              text: '"Because nobody in this house knows what you are."',
            },
          ],
        },
      ],
      decision: {
        prompt: 'The room waits on you.',
        options: [
          {
            id: 'r5-stand',
            label: '"Say nothing. Stand for the count."',
            say: '"…"',
            react:
              '"Asked before the whole house, and he offers— note it down, steward. He offers nothing. Note that."',
            reactNpc: 'naoyuki',
            memory: [{ npc: 'naoyuki', warmthDelta: -1, regard: 'unreadable' }],
            flags: ['r5-stood'],
          },
          {
            id: 'r5-account',
            label: '"Give your day, hour by hour."',
            say: '"Give your day, hour by hour."',
            react: '"Say it slower. I am writing."',
            memory: [{ npc: 'genemon', warmthDelta: 0, regard: 'exact' }],
            flags: ['r5-accounted'],
          },
          {
            id: 'r5-search',
            label: '"Offer your corner to be searched."',
            say: '"Offer your corner to be searched."',
            react:
              '"It was searched while Rokusuke fetched you. A mat, a bowl, a whetstone, a mended cord. Nothing of the house\'s past its keep."',
            memory: [{ npc: 'genemon', warmthDelta: 0, regard: 'open-handed' }],
            flags: ['r5-offered-search'],
          },
        ],
      },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'count-resolve',
    scene: {
      id: 'count-resolve',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The kura tally. Who kept it today?"',
        },
        {
          voice: 'narrator',
          text: "Rokusuke has been standing where the lantern light mostly isn't. He comes forward the way a man wades cold water.",
        },
        {
          voice: 'villager',
          speaker: NPC_NAME.rokusuke,
          text: '"Eleven loads in by dark, steward. His mark against each. Last mark at lamp-lighting, and the rice was long cleared by then. He never left the kura side of the yard. That\'s what the board says. The board\'s there to look at."',
        },
        {
          voice: 'narrator',
          text: 'Genemon sets the tally board beside the day-book and reads one against the other. The room watches him do arithmetic.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The packet was counted at the evening rice, in the inner house. From mid-day to lamp-lighting this man was under grain. The book holds no gap with him in it."',
        },
        {
          voice: 'official',
          speaker: NPC_NAME.naoyuki,
          text: '"Then it is someone of the house."',
        },
        {
          voice: 'narrator',
          text: 'The door to the inner corridor slides. Toku stands in it, in her night-clothes, her hair down — a woman nobody sent for. She looks at no one in particular, and at the book last.',
        },
        { voice: 'steward', speaker: NPC_NAME.toku, text: '"It went up at the new moon."' },
        {
          voice: 'narrator',
          text: 'Nobody asks what went up. Nobody asks where. Genemon dips his brush, enters one line, and closes the day-book.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The count is answered. The house is not short. Take the lamps."',
        },
        { voice: 'narrator', text: 'The rows break up around you. Naoyuki passes you last.' },
        {
          voice: 'official',
          speaker: NPC_NAME.naoyuki,
          text: '"You were counted. That is all it was."',
        },
        {
          voice: 'narrator',
          text: 'At the morning board nobody speaks of the night. The hands keep a little more room around you than the work needs — for a day, then two — and then it is spent, needing nothing from you to end.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"From today the book carries you at a day-wage, in mon, paid against each day worked. It was due at the season\'s turn. It is the season\'s turn."',
        },
        {
          voice: 'narrator',
          text: 'At the paddy Rokusuke works the row over from yours, as he did before, no nearer. You thank him for the tally.',
        },
        {
          voice: 'villager',
          speaker: NPC_NAME.rokusuke,
          text: '"Kept it same as any day. It would have read the same if it hanged you."',
        },
        { voice: 'narrator', text: 'He bends back to the row.' },
        {
          voice: 'narrator',
          text: 'That evening the wage enters the day-book: so many mon, and against the entry, where a name would go, a mark. It is still not a name.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'sb-grove',
    scene: {
      id: 'sb-grove',
      voice: 'steward',
      speaker: 'shinnosuke',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: "The grove, past noon. Yesterday's cut stalks lie where the work stopped. The troop is loud up the slope — and under the troop, smaller, a voice that has no business being here.",
        },
        {
          voice: 'narrator',
          text: 'Shinnosuke stands with his back to green bamboo, holding a stripped branch out in front of him the way nobody taught him. The big male is down on the ground. It is closer to the boy than monkeys come when things are going well.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.shinnosuke,
          text: "\"You're the new one. Do they bite? They bite, don't they — don't tell anyone I'm here.\"",
        },
        {
          voice: 'narrator',
          text: 'You come up the slow way, on the uphill side, and stand where the male can weigh you. It goes when going is its own idea. It takes a seed-pouch with it.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.shinnosuke,
          text: '"I had a plan. Did you see it take the pouch? That wasn\'t my fault."',
        },
      ],
      topics: [],
      decision: {
        prompt: 'The boy is looking at you.',
        options: [
          {
            id: 'sb-grove-report',
            label: '"The house will hear it from me. Walk home ahead."',
            say: '"The house will hear it from me. Walk home ahead."',
            react: '"Fine. Tell them. See if I mind."',
            memory: [{ npc: 'kihei', warmthDelta: 1, regard: 'post-kept' }],
            flags: ['sb-grove-reported'],
          },
          {
            id: 'sb-grove-cover',
            label: '"I count one pouch lost. That\'s all I count."',
            say: '"I count one pouch lost. That\'s all I count."',
            react:
              "\"You won't say? Why won't you say — you're not so bad. Don't expect anything.\"",
            memory: [{ npc: 'shinnosuke', warmthDelta: 1, regard: 'kept-quiet' }],
            flags: ['sb-grove-covered'],
          },
          {
            id: 'sb-grove-teach',
            label: '"Stand uphill of them. Watch."',
            say: '"Stand uphill of them. Watch."',
            react: '"Like this? Why uphill — so they can\'t get above you, or so you look bigger?"',
            memory: [{ npc: 'shinnosuke', warmthDelta: 1, regard: 'taught' }],
            flags: ['sb-grove-taught'],
          },
        ],
      },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'sb-bon',
    scene: {
      id: 'sb-bon',
      voice: 'steward',
      speaker: 'ohisa',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'Loads cross the forecourt all morning: trays, lamp-oil, cut flowers, a bundle of incense sticks O-Yae carries two-handed like a caught bird. Everyone is given something.',
        },
        { voice: 'narrator', text: 'The third load goes past before you ask it.' },
        { voice: 'player', text: '"What do I carry?"' },
        {
          voice: 'steward',
          speaker: NPC_NAME.ohisa,
          text: '"Not this. If a year from now — well. Not this."',
        },
        {
          voice: 'narrator',
          text: 'The house files toward the alcove corridor. The forecourt is yours all day. For once, nobody crosses it.',
        },
        {
          voice: 'narrator',
          text: "At dusk, from the weir path: O-Ume sets her lantern on the water and talks it out past the reeds, the way you'd see a guest to the road.",
        },
        {
          voice: 'narrator',
          text: 'At the gate, the monk Iori has his lodging and his one small fire.',
        },
        {
          voice: 'monk',
          speaker: NPC_NAME.iori,
          text: '"Sit. Two bowls, one mouth. I\'ll be gone before you learn my face."',
        },
        {
          voice: 'narrator',
          text: 'Through the corridor screen, past shoulders: a small tray set out for somebody, and straw sandals beside it, toes toward the gate.',
        },
        {
          voice: 'narrator',
          text: 'Morning. The trays are cleared, the lanterns down, the monk gone north. The sandals are new.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'season-exit', season: 'bon' },
    once: true,
  },

  {
    id: 'sb-lease',
    scene: {
      id: 'sb-lease',
      voice: 'villager',
      speaker: 'matsuzo',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'Matsuzō comes up from the river at the pace of a man nothing has hurried in sixty years. Genemon meets him in the forecourt with the day-book already open — which is how you learn the house knew it was short before the knock.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The weir lease. Due today: rice, one koku. In the store, against it: seven to. The house is short a third, and I will not count it smaller than it is."',
        },
        {
          voice: 'villager',
          speaker: NPC_NAME.matsuzo,
          text: '"Screens took a beating in the spring water. Rats after. Hard year on everything downstream."',
        },
        {
          voice: 'narrator',
          text: "He does not say: on you too. He looks at the gate's paint, and at you, and back toward the river, the way he looks at everything — weather that will pass over his weir eventually.",
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"Three to owing. I ask the season\'s grace on it. The house asks."',
        },
        {
          voice: 'narrator',
          text: 'Genemon bows. Not deeply — the exact depth a Kurosawa steward has never before had to measure for a weir-keeper, measured now, got right the first time because he will not let it be seen twice.',
        },
        {
          voice: 'villager',
          speaker: NPC_NAME.matsuzo,
          text: '"The river can wait a season. The screens can\'t — rats are in them now. Send your man down while the year turns and we\'ll call it even weight by winter."',
        },
        {
          voice: 'narrator',
          text: 'Nobody asks you. A line in the day-book, a nod toward the water, and a season of your evenings is settled between two old men. Three to of rice. It is the first time you have been worth a number. The number is small.',
        },
        {
          voice: 'narrator',
          text: 'At the gate the old man stops, level with you, and looks at you the way he looked at the paint. He goes back down the way he came, having made nothing of it. He has been making nothing of it for a year.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'sb-dog',
    scene: {
      id: 'sb-dog',
      voice: 'arms',
      speaker: 'kihei',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The last of the pack went over the wall two days ago. This one stayed. An old dog, grey to the jaw, one ear ragged from winters, lying in the dead leaves under the far fruit trees like the orchard is a post it was never relieved from.',
        },
        {
          voice: 'narrator',
          text: 'It does not run and it does not beg. When you work, it watches. When you eat, it watches the food, then you — and then it looks away first.',
        },
        {
          voice: 'narrator',
          text: 'Kihei crosses the cleared ground on his round and stops beside you, reading the dog the way he reads a blade for rust.',
        },
        {
          voice: 'arms',
          speaker: NPC_NAME.kihei,
          text: '"The old one. Too slow for the pack, too proud for the village middens. Drive it, feed it, or bring it to me. Don\'t leave it half-decided."',
        },
      ],
      topics: [],
      decision: {
        prompt: 'The dog watches you decide.',
        options: [
          {
            id: 'sb-dog-drive',
            label: '"The orchard\'s cleared. All of it."',
            say: '"The orchard\'s cleared. All of it."',
            react: '"Cleared is cleared. Stop the gap in the wall behind it."',
            memory: [{ npc: 'kihei', warmthDelta: 1, regard: 'thorough' }],
            flags: ['sb-dog-driven'],
          },
          {
            id: 'sb-dog-feed',
            label: '"It stays. I\'ll feed it from my share."',
            say: '"It stays. I\'ll feed it from my share."',
            react:
              '"A dog. Old, one ear. Rice: a handful, mornings, from your measure — entered. If it earns, it stays entered."',
            reactNpc: 'genemon',
            memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'accountable' }],
            flags: ['sb-dog-fed'],
          },
          {
            id: 'sb-dog-kihei',
            label: '"Take it to Kihei."',
            say: '"Take it to Kihei."',
            react: '"Give me your belt-cord. Go rake the leaves. This isn\'t a lesson."',
            memory: [{ npc: 'kihei', warmthDelta: 1, regard: 'clear-eyed' }],
            flags: ['sb-dog-ended'],
          },
        ],
      },
    },
    trigger: { kind: 'flag', flag: 'quest_orchard_chain_done' },
    once: true,
  },

  {
    id: 'sb-dog-coda',
    scene: {
      id: 'sb-dog-coda',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: "New moon, and the round goes out into it. Rats in the store: cleared. The store settles. Then, at the yard's far edge, a lantern crosses with its light hooded, going upstream, unhurried, sure of the path.",
        },
        {
          voice: 'narrator',
          text: 'The rats said nothing. The marten, the time it came, said nothing. The dog barks once — once — at the lantern, and then is quiet.',
        },
        {
          voice: 'narrator',
          text: 'The lantern does not stop. Kihei does not turn his head. The dog watches it the whole way upstream, and nobody enters a bark in any book.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'sb-crest',
    scene: {
      id: 'sb-crest',
      voice: 'steward',
      speaker: 'shinnosuke',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'Loading at the kura. Shinnosuke is up on the stacked bales, where he is not allowed, counting something over your head.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.shinnosuke,
          text: '"Count them. The storehouse has one more than the gate. Why does the storehouse get one more petal than the gate? Who changes a crest — you can\'t just change a crest, can you?"',
        },
        {
          voice: 'narrator',
          text: "You look. Above the seal-plate the mon is cut deep and old: the same flower as the gate's, and not the same flower. You count twice, because counting is the one kind of reading you trust.",
        },
        { voice: 'player', text: '"Ask the steward."' },
        {
          voice: 'steward',
          speaker: NPC_NAME.shinnosuke,
          text: '"I asked everyone. Grandmother said mind the lamp. Genemon wrote in his book. Mother said if I\'ve time for petals I\'ve time for copywork. Nobody answers me. You ask. You work here — they have to answer you."',
        },
        {
          voice: 'narrator',
          text: 'Asking costs you something every time. You pay it at the board that evening.',
        },
        { voice: 'player', text: '"The kura\'s crest. It has one petal more than the gate\'s."' },
        {
          voice: 'narrator',
          text: 'It is not even a question, the way you say it. Genemon looks at you the way he looked at the river-wet stranger the first morning — a thing to be entered under the correct heading. Then he opens the day-book, writes — not long — and closes it.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The kura wants its hinges oiled before the rains. Take the small ladder."',
        },
        {
          voice: 'narrator',
          text: 'That is the answer. It is the same answer the boy got, in a different hand.',
        },
        {
          voice: 'narrator',
          text: 'Shinnosuke is waiting on the woodpile to hear what you learned. You tell him: hinges. He nods like a man twice his age confirming a debt gone bad.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.shinnosuke,
          text: '"See. You\'re the other one they don\'t tell. What did he write — did you see what he wrote?"',
        },
        {
          voice: 'narrator',
          text: "You didn't. The boy climbs down and goes in to his copywork, and you oil the hinges under the crest, and the kura keeps whatever it keeps.",
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'r2-yard-hand',
    scene: {
      id: 'r2-yard-hand',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: "First light. The broom stands against the gatepost where you left it. The hand who kept the yard's round quit for the lowlands; nobody has said whose it is now.",
        },
        { voice: 'narrator', text: 'You take the broom. Nobody takes it back.' },
        {
          voice: 'narrator',
          text: 'The round takes the morning: the gate swept, the forecourt raked, the water in, the wood up off the ground. Dusk round, then the kitchen threshold, the bowl. Genemon comes past with the day-book under his arm and does not slow.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"Rice from the new moon. One measure the day, dry, weighed at the kura door. Meals were terms for a man staying a week."',
        },
        { voice: 'narrator', text: 'He is through the door before you can set the bowl down.' },
        { voice: 'narrator', text: 'A meal ends with the eating. Rice keeps.' },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'rung', rung: 'R2' },
    once: true,
  },

  {
    id: 'r7-dream',
    scene: {
      id: 'r7-dream',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The woodshed, late. On the doorstep, folded under a stone against the wind: his straw coat, mended at the shoulder in small even stitches that no one will own to. He carries it in, lies down on the mat, and for once sleeps at once.',
        },
        { voice: 'narrator', text: '— a road, climbing. Rain on it.' },
        {
          voice: 'narrator',
          text: 'The weight across his shoulders is right. Two loads and the pole, and his feet know the next stone without being asked. Below the road, water — he cannot see it, but it is there the way a wall is there in a dark room.',
        },
        {
          voice: 'narrator',
          text: 'Behind him on the climb, somebody calls a name. Short, worn smooth, thrown easy — a name that has landed a thousand times and expects to land now.',
        },
        { voice: 'narrator', text: 'He turns to answer.' },
        { voice: 'narrator', text: 'The mountain moves.' },
        {
          voice: 'narrator',
          text: 'After that there is only the water, and the road going away upward, and the voice still calling — smaller, and smaller. He knows the name is his. He cannot hear it.',
        },
        {
          voice: 'narrator',
          text: 'He wakes in the woodshed with his mouth open around a word that is not there. Rain, real rain, small on the roof. The coat is where he left it.',
        },
        { voice: 'player', speaker: NAMES.useName, text: '"There was a road."' },
        {
          voice: 'narrator',
          text: 'Nobody answers. It is a long while before he sleeps again, and the dream, that night, does not come back.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'turn-winter',
    scene: {
      id: 'turn-winter',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The snow quits the valley floor first — gray at the edges, then standing water in the wheel-ruts. The ridge keeps its white and means to keep it.',
        },
        {
          voice: 'narrator',
          text: "The river runs black and narrow between its shelves of ice. Past the rope, the old garden's snow lies whole: a winter's worth, and not one track across it.",
        },
        {
          voice: 'narrator',
          text: 'The light stretches. Work that used to end at the woodshed door now ends past it, and the cold stays on, but it has stopped arguing.',
        },
        {
          voice: 'narrator',
          text: 'Small on the road below, a man comes up from the village with new straw rope coiled over his shoulder, for the gates. The year is going out. The New Year comes up the valley behind him.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'season-exit', season: 'winter' },
    once: true,
  },

  {
    id: 'turn-new-year',
    scene: {
      id: 'turn-new-year',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'Ice lets go of the shaded bank a plate at a time, each one turning once in the current and gone.',
        },
        {
          voice: 'narrator',
          text: 'The ground gives underfoot where all winter it rang. The well-path grows a margin of mud, and the mud holds each footprint till noon.',
        },
        {
          voice: 'narrator',
          text: 'Up the woodlot, the bark of the young trees is chewed to the pale — deer, down off the ridge in the dark and gone again by light. Whatever they came for, the ridge no longer had it.',
        },
        {
          voice: 'narrator',
          text: 'Last, and small under all that water-light, the villagers walk their field-bunds, looking at what the thaw left them. Spring is on the valley, and everything from here on is work.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'season-exit', season: 'new-year' },
    once: true,
  },

  {
    id: 'turn-spring',
    scene: {
      id: 'turn-spring',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The green closes ranks. What was rows in the paddies is one surface now, and when the wind crosses it, the whole valley floor moves together.',
        },
        {
          voice: 'narrator',
          text: 'The river drops slow over its gravel, clear and warm in the shallows by noon.',
        },
        {
          voice: 'narrator',
          text: 'In the grove, the new bamboo has gone up taller than a man in a fortnight — pale among the old canes, then not.',
        },
        {
          voice: 'narrator',
          text: 'People show late in this weather, and low: hats in the deep green, moving slow along the rows. Summer has the valley now.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'season-exit', season: 'spring' },
    once: true,
  },

  {
    id: 'turn-summer',
    scene: {
      id: 'turn-summer',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The heat loosens its grip at the edges first: a haze on the river that burns off later each morning, and at dusk the cicadas trade their noon voice for the thin evening one.',
        },
        {
          voice: 'narrator',
          text: 'The light comes in slantwise now and finds the dust hanging in the forecourt air. The ridge stands closer in it, the way it does before weather.',
        },
        {
          voice: 'narrator',
          text: "On the hill above the village, small figures move among the stones with sickles and water-buckets, tidying — one household first, then by week's end all of them.",
        },
        {
          voice: 'narrator',
          text: 'Smoke stands over the village at dusk, more fires than there are suppers. Bon is here, and the valley is making itself ready to be visited.',
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'season-exit', season: 'summer' },
    once: true,
  },

  {
    id: 'turn-bon',
    scene: {
      id: 'turn-bon',
      voice: 'narrator',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The river carries the first leaves down from the ridge, one and two at a time, red before anything on the valley floor has thought of turning.',
        },
        {
          voice: 'narrator',
          text: 'The mornings come in cold enough to name. Mist stands knee-deep in the paddies, and the heavy heads of rice bow through it, all leaning the same way.',
        },
        {
          voice: 'narrator',
          text: 'The road empties back out over the pass. The valley keeps what belongs to it and lets the rest go.',
        },
        {
          voice: 'narrator',
          text: "From the village at dusk, the sound of whetstones — one, then several, no hurry in any of them. Bon has gone home. It is autumn, and the valley's whole year is standing out in the fields, waiting to be carried in.",
        },
      ],
      topics: [],
      decision: { prompt: '', options: [] },
    },
    trigger: { kind: 'season-exit', season: 'bon' },
    once: true,
  },

  {
    id: 'works-intro',
    scene: {
      id: 'works-intro',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: "The board, an evening some weeks into the rice terms. The day's wages are footed and the book should be closed, but Genemon has turned instead to a page ruled off from the rest — older ink at the head of it, this morning's at the foot.",
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The wages are settled. This is the other page."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"Gate: the west post takes water at the foot, second winter running. Paddies: three bunds slumped, the water going through them at its own level. Woodshed: rain through the north corner of the roof, over where you sleep. None of that is news to the land. It is only now news to the book."',
        },
        {
          voice: 'narrator',
          text: 'He enters nothing. The lines are already there, dated, the way a man records frost.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"And one line the house carries whether I write it or not. The weir screens are Matsuzō\'s — leased, and the keeping of them ours. River rats gnaw them at the waterline; every slat through is coin owed across the water. The river feeds the rats and the house pays the difference. That account was open before the river gave you up at the weir."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"I commission nothing that has not been looked at. Walk the three, and the weir path, and look with your own eyes. Rot reads better by daylight. The book will keep."',
        },
      ],
      topics: [
        {
          id: 'works-intro-ask-a',
          label: '"Why now? It\'s stood this long."',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Stood is the wrong word. It has been going, at the pace rot keeps, which is slow enough that a busy man calls it standing. There is ground on the far side of the orchard where nobody argued with the rain. Roofs, once. The land entered it line by line, and no hand wrote anything against it. I do not intend to copy that page."',
            },
          ],
        },
        {
          id: 'works-intro-ask-b',
          label: '"Whose coin mends it?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Not the house\'s; the house\'s coin is spoken for before it reaches the yard — you were told that the first morning, and it has not improved. What goes against the land here is labour, and whatever coin the labourer chooses to set beside it. The book credits what is put in, under the name that put it."',
            },
          ],
        },
      ],
      decision: {
        prompt: 'The page is read out. What do you do with it?',
        options: [
          {
            id: 'works-intro-go',
            label: '"I\'ll walk it today, while the light holds."',
            say: '"I\'ll walk it today, while the light holds."',
            react:
              '"Then the page has an answerer. Take it in the land\'s own order — water first. It is the oldest hand the land has."',
            memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'walked-the-book' }],
            flags: ['works-named-u1', 'works-named-weir'],
          },
          {
            id: 'works-intro-hold',
            label: '"The land got there first. It can wait for me a day."',
            say: '"The land got there first. It can wait for me a day."',
            react:
              '"It can. It will not return the courtesy. The lines stand entered; walk them when you walk them, and know the land is not resting meanwhile."',
            flags: ['works-named-u1', 'works-named-weir'],
          },
        ],
      },
    },
    trigger: { kind: 'scripted' },
    once: true,
  },

  {
    id: 'works-u1',
    scene: {
      id: 'works-u1',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'The board again. Genemon watches you cross the forecourt, sets the day-book open at the ruled-off page, and takes up the brush before you have spoken.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"You walked it. Your face says the page is honest."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"Then hear the work. Post, one, drawn and reset on dry footing. Bunds, three, cut back to sound earth and rammed. Roof, one corner — boards and thatch. One work, the three together. The land does not press in single file, and I will not mend in it."',
        },
        {
          voice: 'narrator',
          text: 'He rules the line and leaves it open, the brush held off the paper.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"Taken up, it goes against your name — your labour, and any coin you put beside it. The book credits what is put in."',
        },
      ],
      topics: [
        {
          id: 'works-u1-ask-a',
          label: '"Why the three together?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Because they are one entry on the land\'s side, whatever they are on ours. Water at the post, water through the bunds, water through the roof. Mend one and the water keeps its other appointments. Close the account whole or watch it reopen."',
            },
          ],
        },
        {
          id: 'works-u1-ask-b',
          label: '"Where do I begin?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Water first, always. The post and the bunds while the weather holds; the roof before the autumn rains, or the autumn will inspect the work for us. The land sets the order. I only copy it."',
            },
          ],
        },
      ],
      decision: {
        prompt: 'The line is ruled and open.',
        options: [
          {
            id: 'works-u1-begin',
            label: '"Set it against my name."',
            say: '"Set it against my name."',
            react:
              '"Entered. Three concerns, one work, your name on the answering side. It is the first entry this page has carried against the land in some years. We will see whose line stands."',
            memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'set-to-it' }],
            flags: ['works-open-u1'],
          },
          {
            id: 'works-u1-hold',
            label: '"When the yard can spare me. It\'s slow work, losing."',
            say: '"When the yard can spare me. It\'s slow work, losing."',
            react:
              '"Slow is the land\'s own gait; do not admire it too long. The line stays open. So, be sure, does the land\'s."',
            flags: ['works-open-u1'],
          },
        ],
      },
    },
    trigger: { kind: 'flag', flag: 'works-seen-u1' },
    once: true,
  },

  {
    id: 'works-u2',
    scene: {
      id: 'works-u2',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: "Genemon finds you at the forecourt with the orchard's brambles still on your sleeves, and does not ask where you have been. The day-book comes to the ruled page as if it had been waiting open.",
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The orchard. That is the land\'s longest entry on this side of the wall — taken a row at a time, over years, and no hand wrote a word against it. Trees a household set out with paths in mind, gone under the choke, and dogs denned in the hollow where fruit was dried once."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The work: the wild rows cut back to the sound wood, and the dens broken and emptied. Not a mending — a taking-back. The land will contest it."',
        },
      ],
      topics: [
        {
          id: 'works-u2-ask-a',
          label: '"The dogs first, or the cutting?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"The dens first, or every arm that swings a billhook is feeding them. Bold from lean winters — they hold ground the way roots do, by staying. Break the pack and what is left is dogs. Kihei will tell you the same, in fewer words, and drill you for it if you ask him plainly."',
            },
          ],
        },
        {
          id: 'works-u2-ask-b',
          label: '"Is anything left in there worth the taking?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Sound trees under the choke — the stock outlasts the state of it; that is usually the way. Whoever planted them expected paths between the rows, and lanterns. The land has spent thirty years unwriting that. It did not finish."',
            },
          ],
        },
      ],
      decision: {
        prompt: "The orchard's line stands the oldest on the page.",
        options: [
          {
            id: 'works-u2-begin',
            label: '"Cut it back. Row by row, the way it was lost."',
            say: '"Cut it back. Row by row, the way it was lost."',
            react:
              '"Entered. Ground taken back is a line I have not written since I was a younger man\'s clerk. Mind the dogs, and bring me the rows one at a time. I will strike the land\'s entry as they come."',
            memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'ground-taken-back' }],
            flags: ['works-open-u2'],
          },
          {
            id: 'works-u2-hold',
            label: '"Thirty years of choke. It\'ll keep another season."',
            say: '"Thirty years of choke. It\'ll keep another season."',
            react:
              '"It will keep the way it has kept — by growing. Every season the cutting is a season heavier. The line is entered; take it up before the bramble takes the arithmetic out of your hands."',
            flags: ['works-open-u2'],
          },
        ],
      },
    },
    trigger: { kind: 'flag', flag: 'works-seen-u2' },
    once: true,
  },

  {
    id: 'works-u3',
    scene: {
      id: 'works-u3',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: "Evening at the board. Genemon has the kura's tallies out beside the day-book, one read against the other, and he speaks without looking up — the way he speaks when the figures have already argued the matter.",
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"You have seen it. The kura is sound and full, and those are two different worries. It holds this year\'s rice and not a measure over. A poor year starves the house; a good year would embarrass it — grain standing in the damp for want of a roof. The land collects either way."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"So: a second granary, raised at the kura, on its own footings. Stores past the winter\'s need. Every work on this page till now has answered a loss the land already entered. This one is made before the loss. I have waited a long time to rule a line of that kind."',
        },
      ],
      topics: [
        {
          id: 'works-u3-ask-a',
          label: '"Why a second? Widen the one that stands."',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"The kura is sound, and sound is kept, not cut open. A second stands on its own feet, its own roof, its own lock — two roofs do not fail in one night, and two counts keep each other honest. The land takes buildings one at a time. Give it two."',
            },
          ],
        },
        {
          id: 'works-u3-ask-b',
          label: '"Stores past need — for what, exactly?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"For the year the valley has instead of the year we plan for. The land keeps no schedule and sends no word ahead. A full storehouse is the one argument it hears; everything else is entered under losses. Ask the ground past the orchard how far a lean year reaches when nothing stands in its way."',
            },
          ],
        },
      ],
      decision: {
        prompt: 'The footings can be cut this season or not at all.',
        options: [
          {
            id: 'works-u3-begin',
            label: '"Raise it. Board by board, ahead of the weather."',
            say: '"Raise it. Board by board, ahead of the weather."',
            react:
              '"Entered — and mark it: the first line on this page the land did not write first. Green wood dries crooked; buy seasoned, or cut early and wait. The winter will weigh the work, whichever."',
            memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'past-winters-need' }],
            flags: ['works-open-u3'],
          },
          {
            id: 'works-u3-hold',
            label: '"A whole granary. That\'s past my scale of mending."',
            say: '"A whole granary. That\'s past my scale of mending."',
            react:
              '"It is past mending altogether; that is its virtue. The line is ruled and stands open. Footings before the frost, or the year is lost and the land holds the page another winter."',
            flags: ['works-open-u3'],
          },
        ],
      },
    },
    trigger: { kind: 'flag', flag: 'works-seen-u3' },
    once: true,
  },

  {
    id: 'works-u4',
    scene: {
      id: 'works-u4',
      voice: 'steward',
      speaker: 'genemon',
      motivates: [],
      greeting: [
        {
          voice: 'narrator',
          text: 'Past supper, the board, the lamp low. Genemon has the day-book open to the ruled page, and for once he is not writing — he is reading it back, line by line, the way a man walks a fence he mended himself.',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"Gonbei. Stand where I can see you; this is the last of the page."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"The omoya. You have seen it from the forecourt — half this house shut, moss on the ridge, rooms going back to the land indoors, which is the quiet way: dust, damp, screens that no hand slides. The land does not need weather to take a room. It only needs the room left alone."',
        },
        {
          voice: 'steward',
          speaker: NPC_NAME.genemon,
          text: '"I told you I rule a second reckoning from this season — walls that held, ground taken back, one line a season in plain words. This is that reckoning\'s first great line. The house set in order: roofs sound, screens mended, air and use in every room. Thirty-one years I have written this house\'s losses. The land has kept the better book all that time. I mean to even the ledgers before I am done."',
        },
      ],
      topics: [
        {
          id: 'works-u4-ask-a',
          label: '"Why the omoya last?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Because the rest holds now, and it did not. The gate is dry at the foot, the bunds carry their water, the orchard bears, grain stands past the winter\'s need. The land\'s book against this house is shorter than it has been in thirty years. The omoya is the longest line left on it — and a house is answered from the outside in."',
            },
          ],
        },
        {
          id: 'works-u4-ask-b',
          label: '"What does \'in order\' come to, in the doing?"',
          answer: [
            {
              voice: 'steward',
              speaker: NPC_NAME.genemon,
              text: '"Roof and ridge first — a room is lost from above. Then the shut rooms, opened one at a time: screens repapered, boards taken up where the damp has been under them, braziers lit until the walls forget the wet. Then use. A room is kept by being lived in; there is no other keeping. The doing is long. The entry is one line."',
            },
          ],
        },
      ],
      decision: {
        prompt: 'The last line of the page waits on an answer.',
        options: [
          {
            id: 'works-u4-begin',
            label: '"Open the rooms. All of them."',
            say: '"Open the rooms. All of them."',
            react:
              '"Entered. When it is done, the season-line writes itself, and it will not be a loss. The land may have the far bank and the winter. This ground is spoken for."',
            memory: [{ npc: 'genemon', warmthDelta: 1, regard: 'the-house-stands' }],
            flags: ['works-open-u4'],
          },
          {
            id: 'works-u4-hold',
            label: '"A whole house. Give me the winter to look at it."',
            say: '"A whole house. Give me the winter to look at it."',
            react:
              '"Look, then — but look as the land looks, every day, without tiring. The line is ruled. The omoya has waited years for a hand; it can wait a season more. It should not wait two."',
            flags: ['works-open-u4'],
          },
        ],
      },
    },
    trigger: { kind: 'flag', flag: 'works-seen-u4' },
    once: true,
  },
];
